import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import "./style.scss";
import AttachmentIcon from "../../assets/icons/components/AttachmentIcon";
import { _global } from "../../helpers";
import { RightArrowIcon, DownArrowIcon, ArrowUpIcon, ArrowDownIcon, ArrowUpDownIcon } from "../../assets/icons/components";
import { useDispatch, useSelector } from "react-redux";
import SkeletonText from "../skeletons/SkeletonText";
import actions from "./../../actions/action.conversations";
import { downloadAttachment } from "./../../actions/action.messageList";
import { createPortal, unstable_batchedUpdates } from "react-dom";
import { Alert, Input } from "reactstrap";
import { Button } from "@centivo/react-components";
import DatePicker from "react-datepicker";
import ReactMarkdown from "react-markdown";
import rehypeRaw from 'rehype-raw';
import { changeToTitleCase, formatInAppMessageHtml, getConversationMessageCount } from "../../utils/convertionUtils";
import { exportInboxToExcel } from "./inboxExportExcelJs";
import { collectCompleteExportRows, fetchAllConversationMessages } from "./completeExport";
import { exportCompleteToExcel } from "./completeExportExcelJs";
import { api } from "../../services";
import { canExportMessageReports } from "../../utils/permissionUtils";

const getContent = (path, obj, separator = ".") => {
  const properties = Array.isArray(path) ? path : path.split(separator);
  return properties.reduce((prev, curr) => prev && prev[curr], obj);
};

const CLIENT_NAME_SORT = "clientName";
const INBOX_DATE_SORT = "inboxDate";

const getClientName = (conversation, member) => member?.clientName || conversation?.member?.clientName || "";

const hasDateValue = (value) => value !== null && value !== undefined && value !== "";

// V2 messages self-heal the initial date. Older rows still show the last message date.
const inboxDateValue = (conversation) => {
  const initial = conversation?.currentMemberRequestInitialMessageDate;
  if (hasDateValue(initial)) return initial;
  const last = conversation?.lastMessage?.createdTimestamp;
  return hasDateValue(last) ? last : null;
};

const formatConversationDate = (value) => {
  if (!hasDateValue(value)) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
};

const getSortValue = (path, obj, member) => {
  const raw = path === CLIENT_NAME_SORT
    ? getClientName(obj, member)
    : path === INBOX_DATE_SORT
      ? inboxDateValue(obj)
      : getContent(path, obj);
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") return raw;
  const asDate = moment(raw, moment.ISO_8601, true);
  return asDate.isValid() ? asDate.valueOf() : String(raw).toLowerCase();
};

const compareValues = (aValue, bValue, ascending) => {
  if (aValue === null && bValue === null) return 0;
  if (aValue === null) return 1;
  if (bValue === null) return -1;
  if (aValue === bValue) return 0;
  if (ascending) return aValue < bValue ? -1 : 1;
  return aValue > bValue ? -1 : 1;
};

const DEFAULT_SORT_ATTRIBUTE = "lastMessage.createdTimestamp";
const defaultSortFor = (tab) => (tab === "inbox" ? INBOX_DATE_SORT : DEFAULT_SORT_ATTRIBUTE);
const SECONDARY_SORT_PATHS = {
  [INBOX_DATE_SORT]: DEFAULT_SORT_ATTRIBUTE,
  transactionDate: DEFAULT_SORT_ATTRIBUTE,
  [CLIENT_NAME_SORT]: DEFAULT_SORT_ATTRIBUTE,
};

// Missing primary values stay at the bottom, ordered among themselves by secondaryPath.
const compareByAttribute = (path, ascending, secondaryPath, member) => (a, b) => {
  const aValue = getSortValue(path, a, member);
  const bValue = getSortValue(path, b, member);
  const primary = compareValues(aValue, bValue, ascending);
  if (primary !== 0 || !secondaryPath) return primary;
  return compareValues(getSortValue(secondaryPath, a, member), getSortValue(secondaryPath, b, member), ascending);
};

const conversationSearchText = (conversation, member) => {
  const name = conversation.member
    ? `${conversation.member.firstName || ""} ${conversation.member.lastName || ""}`
    : (member?.name || "");
  const memberId = member?.sequencedMemberId || conversation.member?.sequencedMemberId || "";
  const email = member?.email || conversation.member?.emailAddress || "";
  const phone = member?.phone || conversation.member?.phoneNumber || "";
  const phoneDigits = String(phone).replace(/\D/g, "");
  const lastMessageDate = formatConversationDate(conversation.lastMessage?.createdTimestamp);
  const inboxDate = formatConversationDate(inboxDateValue(conversation));
  const completedDate = formatConversationDate(conversation.transactionDate);
  return [
    inboxDate,
    lastMessageDate,
    completedDate,
    name,
    memberId,
    getClientName(conversation, member),
    email,
    phone,
    phoneDigits,
    conversation.topic,
    conversation.conversationId,
  ].filter(Boolean).join(" ").toLowerCase();
};

const matchesSearch = (conversation, query, member) =>
  conversationSearchText(conversation, member).includes(query);

const ConversationTable = ({ conversations = { Items: [] }, member, openReplyModal, filter, setFilter, completeFromDate, completeToDate, onApplyCompleteDates }) => {
  const [search, setSearch] = useState("");

  const [order, setOrder] = useState(false);
  const [sortingBy, setSortingBy] = useState(() => defaultSortFor(filter));

  const [sourceConversations, setSourceConversations] = useState([]);
  const [sortedConversation, setSortedConversation] = useState([]);

  const [showMessages, setShowMessages] = useState(false);

  //pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [conversationPerPage, setConversationPerPage] = useState(10);

  const indexOfLastConversation = currentPage * conversationPerPage;
  const indexOfFirstConversation = indexOfLastConversation - conversationPerPage;
  const currentConversations = sortedConversation.slice(indexOfFirstConversation, indexOfLastConversation);

  const showLastMessageDate = filter !== "inbox";
  const showInboxDate = filter === "inbox";
  const showCompleteDateFilter = filter === "complete" && !member?.memberUuid;
  const showCompletedDate = filter === "complete";
  const columnCount = 9 + (showLastMessageDate ? 1 : 0) + (showInboxDate ? 1 : 0) + (showCompletedDate ? 1 : 0);

  const [draftFromDate, setDraftFromDate] = useState(() => (completeFromDate ? moment(completeFromDate, "YYYY-MM-DD").toDate() : null));
  const [draftToDate, setDraftToDate] = useState(() => (completeToDate ? moment(completeToDate, "YYYY-MM-DD").toDate() : null));
  const [syncedFromDate, setSyncedFromDate] = useState(completeFromDate);
  const [syncedToDate, setSyncedToDate] = useState(completeToDate);
  if (completeFromDate !== syncedFromDate || completeToDate !== syncedToDate) {
    setSyncedFromDate(completeFromDate);
    setSyncedToDate(completeToDate);
    setDraftFromDate(completeFromDate ? moment(completeFromDate, "YYYY-MM-DD").toDate() : null);
    setDraftToDate(completeToDate ? moment(completeToDate, "YYYY-MM-DD").toDate() : null);
    setSearch("");
    setOrder(false);
    setSortingBy(DEFAULT_SORT_ATTRIBUTE);
    setCurrentPage(1);
  }

  const dispatch = useDispatch();
  const { messages, loading, error, loadingMessages } = useSelector((state) => state.conversations);

  useEffect(() => {
    const nextSource = [...conversations.Items].filter(c => c.lastMessage);
    setSourceConversations(nextSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations.fetchedAt]);

  useEffect(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? sourceConversations.filter((conversation) => matchesSearch(conversation, query, member))
      : [...sourceConversations];
    const nextRows = filtered.sort(compareByAttribute(
      sortingBy,
      order,
      SECONDARY_SORT_PATHS[sortingBy],
      member,
    ));
    setSortedConversation(nextRows);
    const perPage = Number(conversationPerPage) || 10;
    const pageCount = Math.max(1, Math.ceil(nextRows.length / perPage));
    setCurrentPage((page) => Math.min(page, pageCount));
    // Re-filter the loaded rows. Sort changes are applied separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sourceConversations]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const sortTable = (sorting) => {
    unstable_batchedUpdates(() => {
      setOrder(sorting.order);
      setSortingBy(sorting.attribute);
      setSortedConversation(
        [...sortedConversation].sort(compareByAttribute(
          sorting.attribute,
          sorting.order,
          SECONDARY_SORT_PATHS[sorting.attribute],
          member,
        ))
      );
    });
  };

  const setFilterHandler = (tabName) => {
    unstable_batchedUpdates(() => {
      setShowMessages(false);
      setFilter(tabName);
      setCurrentPage(1);
      setSearch("");
      setOrder(false);
      setSortingBy(defaultSortFor(tabName));
    });
  };

  const showMessagesHandler = (conversation) => {
    if (conversation.conversationId === showMessages) {
      //hdie the messages
      setShowMessages(false);
    } else {
      setShowMessages(conversation.conversationId);
      dispatch(actions.getMessagesByConversationIdAction(conversation));
    }
  };

  const changeStatusHandler = (conversation, status) => {
    dispatch(actions.changeConversationStatusAction({ ...conversation, status }, filter));
  };

  const showAttachment = (message, conversation) => {
    return (
      <div className="email-attachments-wrapper">
        {message.attachments &&
          message.attachments.map((attachment) => {
            return (
              <div key={attachment.Key} className="email-attachment">
                <div className="top">
                  <div className="card-icon">{_global.getIconBasedOnFileType(attachment.type)}</div>
                  <div className="file-desc">
                    <p>{attachment.name}</p>
                    <p style={{ fontSize: "x-small" }}>{_global.niceBytes(attachment.size)}</p>
                  </div>
                </div>
                <div className="buttom">
                  <p onClick={() => dispatch(downloadAttachment({ Key: attachment.Key, filename: attachment.name, conversationId: conversation.conversationId, recipientId: conversation.recipientId }))}>Download</p>
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const showMessagesPlaceHodler = (count) => {
    return [...Array(count)].map((e, i) => (
      <div className="message-card" key={i}>
        <div className="message-header">
          <SkeletonText style={{ width: "20%", height: "20px" }} />
          <SkeletonText style={{ width: "20%", height: "20px" }} />
        </div>
        {/* <SkeletonText style={{ width: '30%', height: '30px' }} /> */}
        <SkeletonText style={{ width: "100%", height: "20px" }} />
      </div>
    ));
  };

  const pagination = (number) => {
    setCurrentPage(number);
  };

  // A ref rather than state so repeat clicks are ignored without re-rendering the button into its disabled style.
  const exportingRef = useRef(false);
  const completeExportingRef = useRef(false);
  const [completeExporting, setCompleteExporting] = useState(false);
  const [completeExportError, setCompleteExportError] = useState(null);
  const searchInputRef = useRef(null);
  const exportHandler = async () => {
    if (!canExportMessageReports() || exportingRef.current) return;
    exportingRef.current = true;
    try {
      await exportInboxToExcel(sortedConversation, member);
    } catch (e) {
      console.error("Inbox export failed", e);
    } finally {
      exportingRef.current = false;
    }
  };

  useEffect(() => {
    if (!completeExporting) return undefined;
    const blockKeys = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener("keydown", blockKeys, true);
    document.addEventListener("keyup", blockKeys, true);
    return () => {
      document.removeEventListener("keydown", blockKeys, true);
      document.removeEventListener("keyup", blockKeys, true);
    };
  }, [completeExporting]);

  const completeExportHandler = async () => {
    if (!canExportMessageReports() || completeExportingRef.current) return;
    completeExportingRef.current = true;
    setCompleteExporting(true);
    setCompleteExportError(null);
    try {
      const messagesByConversationId = await fetchAllConversationMessages(
        sortedConversation,
        api.getMessagesByConversationId,
      );
      const exportRows = collectCompleteExportRows(
        sortedConversation,
        messagesByConversationId,
        member,
        showCompleteDateFilter ? completeFromDate : null,
        showCompleteDateFilter ? completeToDate : null,
      );
      await exportCompleteToExcel(exportRows);
    } catch (e) {
      console.error("Complete export failed", e);
      setCompleteExportError(e?.message || "The complete export failed. Please try again.");
    } finally {
      completeExportingRef.current = false;
      setCompleteExporting(false);
    }
  };

  return (
    <div className="conversationTable">
      <Alert color="danger" isOpen={Boolean(completeExportError || error)} style={{ marginTop: "20px" }}>
        {completeExportError || error}
      </Alert>
      {completeExporting && createPortal(
        <div className="complete-export-overlay" role="alertdialog" aria-modal="true" aria-label="Generating report">
          <div className="complete-export-overlay-content">
            <div className="complete-export-spinner" />
            <p>Generating report...</p>
          </div>
        </div>,
        document.body,
      )}
      <div className="tab-options">
        <div className={`tab-action ${filter === "inbox" ? " selected" : null}`} onClick={() => setFilterHandler("inbox")}>
          Inbox
        </div>
        <div className={`tab-action ${filter === "complete" ? " selected" : null}`} onClick={() => setFilterHandler("complete")}>
          Complete
        </div>
        {canExportMessageReports() && filter === "inbox" && (
          <div className="export-btn-wrapper">
            <Button
              type="button"
              className="inboxExportButton"
              isDisabled={!sortedConversation.length}
              onClick={exportHandler}
            >
              Export
            </Button>
          </div>
        )}
        {canExportMessageReports() && filter === "complete" && (
          <div className="export-btn-wrapper">
            <Button
              type="button"
              className="inboxExportButton"
              isDisabled={!sortedConversation.length || completeExporting}
              onClick={completeExportHandler}
            >
              Export
            </Button>
          </div>
        )}
      </div>

      <div className='content-wrapper'>
        <div className="conversation-toolbar">
        <div className="conversation-toolbar-left">
          <div className="conversation-search-wrapper">
            <Input
              id="conversationSearch"
              type="text"
              className="conversation-search-input"
              placeholder="Search messages"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search messages"
              innerRef={searchInputRef}
            />
            {search && (
              <button
                type="button"
                className="conversation-search-clear"
                aria-label="Clear search"
                onClick={() => {
                  setSearch("");
                  if (searchInputRef.current) searchInputRef.current.focus();
                }}
              >
                <i className="icon icon-x-circle" />
              </button>
            )}
          </div>
        </div>
        <div className="conversation-toolbar-right">
        <Pagination
          totalConversations={sortedConversation.length}
          currentConversations={currentConversations}
          conversationPerPage={conversationPerPage}
          indexOfFirstConversation={indexOfFirstConversation}
          pagination={pagination}
          setConversationPerPage={setConversationPerPage}
          currentPage={currentPage}
          showCompleteDateFilter={showCompleteDateFilter}
          draftFromDate={draftFromDate}
          draftToDate={draftToDate}
          setDraftFromDate={setDraftFromDate}
          setDraftToDate={setDraftToDate}
          onApplyCompleteDates={onApplyCompleteDates}
        />
        </div>
        </div>

        <table>
          <thead>
            <tr>
              <th></th>
              {showLastMessageDate && (
                <th
                  onClick={() => {
                    sortTable({ order: !order, attribute: "lastMessage.createdTimestamp" });
                  }}
                >
                  Last Message Date
                  {sortingBy === "lastMessage.createdTimestamp" ? order ? <ArrowUpIcon /> : <ArrowDownIcon /> : <ArrowUpDownIcon />}
                </th>
              )}
              {showCompletedDate && (
                <th
                  onClick={() => {
                    sortTable({ order: !order, attribute: "transactionDate" });
                  }}
                >
                  Completed Date
                  {sortingBy === "transactionDate" ? order ? <ArrowUpIcon /> : <ArrowDownIcon /> : <ArrowUpDownIcon />}
                </th>
              )}
              {showInboxDate && (
                <th
                  onClick={() => {
                    sortTable({ order: !order, attribute: INBOX_DATE_SORT });
                  }}
                >
                  Date
                  {sortingBy === INBOX_DATE_SORT ? order ? <ArrowUpIcon /> : <ArrowDownIcon /> : <ArrowUpDownIcon />}
                </th>
              )}
              <th></th>
              <th>MEMBER NAME</th>
              <th
                onClick={() => {
                  sortTable({ order: !order, attribute: CLIENT_NAME_SORT });
                }}
              >
                CLIENT NAME
                {sortingBy === CLIENT_NAME_SORT ? order ? <ArrowUpIcon /> : <ArrowDownIcon /> : <ArrowUpDownIcon />}
              </th>
              <th>MEMBER ID</th>
              <th>VENDOR</th>
              <th>MEMBER EMAIL</th>
              <th>PHONE</th>
              <th style={{ width: '400px' }}>subject</th>
            </tr>
          </thead>
          <tbody>
            {currentConversations.map((conversation, index) => {
              return (
                <React.Fragment key={conversation.conversationId}>
                  <tr onClick={() => showMessagesHandler(conversation)} className={showMessages === conversation.conversationId ? 'selected-row' : ''}>
                    <td className="icon-arrow">
                      {getConversationMessageCount(conversation) > 1 ? showMessages === conversation.conversationId ? <DownArrowIcon /> : <RightArrowIcon /> : null}
                    </td>
                    {showLastMessageDate && (
                      <td>
                        {moment(conversation.lastMessage.createdTimestamp).format("MM/DD/YYYY")}
                      </td>
                    )}
                    {showCompletedDate && (
                      <td>
                        {conversation.transactionDate
                          ? moment(conversation.transactionDate).format("MM/DD/YYYY")
                          : "-"}
                      </td>
                    )}
                    {showInboxDate && (
                      <td>
                        {formatConversationDate(inboxDateValue(conversation)) || "-"}
                      </td>
                    )}
                    <td>
                      {
                        (!isNaN(conversation.lastMessage.attachments) && conversation.lastMessage.attachments > 0) ||
                        (Array.isArray(conversation.lastMessage.attachments) && conversation.lastMessage.attachments.length) 
                          ? <AttachmentIcon /> 
                          : ""
                      }
                    </td>
                    <td>
                      {
                        conversation.member 
                          ? changeToTitleCase(conversation.member.firstName + " " + conversation.member.lastName) 
                          : member 
                            ? changeToTitleCase(member.name) 
                            : null
                      }
                    </td>
                    <td>
                      {getClientName(conversation, member) || "-"}
                    </td>
                    <td>
                      {member.sequencedMemberId || (conversation.member && conversation.member.sequencedMemberId)}
                    </td>
                    <td>
                      {conversation?.member?.memberHccId || member?.memberHccId ? 'HealthRules Payer' : "Javelina"}
                    </td>
                    <td>
                      {member.email || (conversation.member && conversation.member.emailAddress)}
                    </td>
                    <td>
                      {_global.formatPhoneNumber(member.phone || (conversation.member && conversation.member.phoneNumber))}
                    </td>
                    <td>{conversation.topic}</td>
                  </tr>

                  {showMessages === conversation.conversationId ? (
                    <tr className="expanded">
                      <td colSpan={columnCount}>
                        <div className="message-cards">
                          {messages?.Items?.length > 0 && messages.Items.length !== getConversationMessageCount(conversation) && messages.LastEvaluatedKey && (
                            <div
                              className="load-more-btn load-older-messages"
                              onClick={() => dispatch(actions.getMessagesByConversationIdAction(conversation, messages.LastEvaluatedKey))}
                            >
                              Load older...
                            </div>
                          )}
                          {loadingMessages && messages?.Items?.length > 0 && showMessagesPlaceHodler(1)}
                          {messages && messages.Items.length
                            ? messages.Items.map((message, index) => (
                              <div className="message-card" key={message.messageId + index}>
                                <div className="message-header">
                                  <div className="senderFullName"><p>{message.senderFullName}</p></div>

                                  <div className="createdTimestamp">{moment(message.createdTimestamp).format("MMMM Do, YYYY hh:mm a")}</div>
                                </div>
                                {message.attachments ? showAttachment(message, conversation) : null}
                                <div className="message-body">
                                  <ReactMarkdown rehypePlugins={[rehypeRaw]} children={formatInAppMessageHtml(message.message)} />
                                </div>
                              </div>
                            ))
                            : showMessagesPlaceHodler(getConversationMessageCount(conversation) - 1)}
                          {loadingMessages && !messages?.Items?.length && showMessagesPlaceHodler(1)}
                          <div className="action-row">
                            <div className="reply-btn-wrapper">
                              <button
                                className="btn btn-transparent btn-reply"
                                onClick={() => {
                                  openReplyModal(conversation);
                                }}
                              >
                                <span className="icon icon-corner-up-left" />
                                Reply
                              </button>
                            </div>
                            {filter === "inbox" && (
                              <div
                                className="move-message-container"
                                onClick={() => {
                                  changeStatusHandler(conversation, "COMPLETE");
                                }}
                              >
                                <input type="checkbox" className="move-message-checkbox" />
                                <i className={`icon icon-circle icon-circle-unchecked`} />
                                <label className="move-message-label">Move to complete</label>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {conversations.LastEvaluatedKey && (
          <div
            className="load-more-btn"
            onClick={() => {
              setShowMessages(false);
              if (member.memberUuid) {
                dispatch(actions.getConversationsByRecipientIdAction(member.memberUuid, conversations.LastEvaluatedKey, filter));
              } else {
                if (filter === "inbox") {
                  dispatch(actions.getConversationsByStatusAction("CSR_ACTION_REQUIRED", conversations.LastEvaluatedKey, filter));
                } else if (filter === "complete") {
                  dispatch(actions.getCompleteConversationsByDateAction(completeFromDate, completeToDate));
                }
              }
            }}
          >
            {loading ? "Loading" : " Load more..."}
          </div>

        )}
      </div>
    </div>
  );
};

export default ConversationTable;

const Pagination = ({
  conversationPerPage,
  currentConversations,
  totalConversations,
  indexOfFirstConversation,
  pagination,
  setConversationPerPage,
  currentPage,
  showCompleteDateFilter,
  draftFromDate,
  draftToDate,
  setDraftFromDate,
  setDraftToDate,
  onApplyCompleteDates,
}) => {
  const pageNumbers = [];

  for (let i = 1; i <= Math.ceil(totalConversations / conversationPerPage); i++) {
    pageNumbers.push(i);
  }

  const applyDisabled = !draftFromDate || !draftToDate || moment(draftFromDate).startOf("day").isAfter(moment(draftToDate).startOf("day"));

  return (
    <nav className={`pagination-navbar${showCompleteDateFilter ? " with-date-filter" : ""}`}>
      {showCompleteDateFilter && (
        <div className="complete-date-filter">
          <label htmlFor="complete-from-date">From</label>
          <DatePicker
            id="complete-from-date"
            selected={draftFromDate}
            onChange={setDraftFromDate}
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            className="complete-date-input"
            autoComplete="off"
          />
          <label htmlFor="complete-to-date">To</label>
          <DatePicker
            id="complete-to-date"
            selected={draftToDate}
            onChange={setDraftToDate}
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            className="complete-date-input"
            autoComplete="off"
          />
          <Button
            type="button"
            className="complete-date-apply"
            isDisabled={applyDisabled}
            onClick={() => onApplyCompleteDates(moment(draftFromDate).format("YYYY-MM-DD"), moment(draftToDate).format("YYYY-MM-DD"))}
          >
            Apply
          </Button>
        </div>
      )}
      <span>      Records: {indexOfFirstConversation + 1} - {+indexOfFirstConversation + +currentConversations.length} / {totalConversations}</span>

      <select
        id="dropdown"
        onChange={(e) => {
          pagination(1);
          setConversationPerPage(e.target.value);
        }}
      >
        <option value="10">10</option>
        <option value="25">25</option>
        <option value="50">50</option>
        <option value="100">100</option>
      </select>
      <span>per page</span>
      <span>    {currentPage} / {pageNumbers.length}</span>

      <div className='pagination-arrow-botton-wrapper'>
        <div onClick={() => currentPage <= 1 ? null : pagination(currentPage - 1)}>
          <i className="icon icon-chevron-left" />
        </div>
        <div onClick={() => currentPage < pageNumbers.length ? pagination(currentPage + 1) : null}>
          <i className="icon icon-chevron-right" />
        </div>
      </div>

    </nav>
  );
};
